import { Copy } from '@app/components/various/Icons';
import { Tooltip } from '@app/components/various/Tooltip';
import { copyToClipboard } from '@app/utils/clipboard';
import { handleSpaceAndEnter } from '@app/utils/handleEnterKey';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';

const TOOLTIP_HIDE_DELAY = 2000;

interface Props {
  inputRef: React.RefObject<HTMLInputElement>;
  onClick?: () => void;
}

export const InputCopyButton = React.forwardRef<HTMLDivElement, Props>(({ inputRef, onClick }, ref) => {
  const { t } = useTranslation(['common']);
  const [isCopied, setIsCopied] = React.useState(false);
  const timeoutRef = React.useRef<number>();

  const handleClick = () => {
    const value = inputRef.current?.value;

    if (!isDefined(value)) {
      return;
    }

    window.clearTimeout(timeoutRef.current);
    setIsCopied(true);
    copyToClipboard(value);
    onClick?.();

    timeoutRef.current = window.setTimeout(() => {
      setIsCopied(false);
    }, TOOLTIP_HIDE_DELAY);
  };

  React.useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div
      role="button"
      ref={ref}
      tabIndex={0}
      aria-label={t('common:copy')}
      className="ml-1 flex rounded-small text-neutral-40 focus-visible:focus-outline"
      onKeyDown={handleSpaceAndEnter(handleClick)}
      onClick={handleClick}
    >
      <Tooltip content={t('common:copied_message')} visible={isCopied}>
        <div className="flex">
          <Copy className="size-5" />
          {isCopied && (
            <span className="sr-only" aria-live="polite">
              {t('common:copied_message')}
            </span>
          )}
        </div>
      </Tooltip>
    </div>
  );
});
